import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { url } from '../components/utils/constants';

const useCurrencyConversion = () => {
  // Taxas de câmbio por pares de moeda
  const [exchangeRates, setExchangeRates] = useState({
    'ARS_BRL': { rate: 0.005, isActive: true, description: 'Peso Argentino para Real Brasileiro' },
    'BRL_ARS': { rate: 200, isActive: true, description: 'Real Brasileiro para Peso Argentino' }
  });

  // Taxa de serviço única
  const [serviceFee, setServiceFee] = useState(1.5); // Taxa única em porcentagem
  const [serviceFeeActive, setServiceFeeActive] = useState(true); // Se a taxa de serviço está ativa

  // Par de moeda selecionado no Step1
  const [selectedCurrencyPair, setSelectedCurrencyPair] = useState('ARS_BRL');

  const [companyFeeSettings, setCompanyFeeSettings] = useState(null);

  // Carregar configurações de taxas da empresa
  const loadCompanyFeeSettings = useCallback(async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${url}/api/v1/fee-settings`, { headers });

      if (response.data.data) {
        const settings = response.data.data;
        setCompanyFeeSettings(settings);

        console.log('🔧 Configurações carregadas:', settings);

        // Atualizar taxa de serviço única
        if (settings.serviceFee) {
          // Se for objeto, pegar o primeiro valor ou usar valor padrão
          if (typeof settings.serviceFee === 'object') {
            const feeValue = settings.serviceFee.BRL || settings.serviceFee.ARS || 1.5;
            setServiceFee(feeValue);
          } else {
            setServiceFee(settings.serviceFee);
          }
        }

        if (settings.serviceFeeActive !== undefined) {
          console.log('🔧 serviceFeeActive carregado:', settings.serviceFeeActive);
          setServiceFeeActive(settings.serviceFeeActive);
        } else {
          console.log('⚠️ serviceFeeActive não encontrado nas configurações');
        }

        // Atualizar taxas de câmbio por pares
        if (settings.exchangeRates) {
          setExchangeRates(settings.exchangeRates);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de taxas:', error);
      // Manter valores padrão em caso de erro
    }
  }, []);

  // Simular busca de cotações em tempo real (em produção seria uma API)
  const fetchExchangeRates = useCallback(async () => {
    try {
      // Simular delay de API
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Simular variação nas cotações
      const variation = (Math.random() - 0.5) * 0.02; // ±1% de variação
      const newRates = {
        ARS_BRL: 0.005 * (1 + variation), // 1 ARS = 0.005 BRL (correto)
        BRL_ARS: 200 * (1 + variation)    // 1 BRL = 200 ARS (correto)
      };

      setExchangeRates(newRates);
    } catch (error) {
      console.error('Erro ao buscar cotações:', error);
    }
  }, []);

  // Atualizar cotações a cada 5 minutos
  useEffect(() => {
    const interval = setInterval(fetchExchangeRates, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchExchangeRates]);

  const convertCurrency = useCallback((amount, currencyPair) => {
    const rate = exchangeRates[currencyPair];
    if (!rate || !rate.isActive) {
      throw new Error(`Taxa de câmbio não disponível para ${currencyPair}`);
    }
    return amount * rate.rate;
  }, [exchangeRates]);

  // Obter taxa de câmbio atual baseada no par selecionado
  const getCurrentExchangeRate = useCallback(() => {
    return exchangeRates[selectedCurrencyPair];
  }, [exchangeRates, selectedCurrencyPair]);

  // Obter moedas do par selecionado
  const getCurrencyPairInfo = useCallback(() => {
    const pair = selectedCurrencyPair;
    if (pair === 'ARS_BRL') {
      return { from: 'ARS', to: 'BRL', fromSymbol: '$', toSymbol: 'R$' };
    } else if (pair === 'BRL_ARS') {
      return { from: 'BRL', to: 'ARS', fromSymbol: 'R$', toSymbol: '$' };
    }
    return null;
  }, [selectedCurrencyPair]);

  const calculateServiceFee = useCallback((amount, currency) => {
    if (!serviceFeeActive) {
      return 0; // Se a taxa de serviço estiver desabilitada, retorna 0
    }
    return (amount * serviceFee) / 100; // Taxa única aplicada sobre qualquer valor
  }, [serviceFee, serviceFeeActive]);

  const calculateTotalWithFee = useCallback((amount, currency) => {
    const fee = calculateServiceFee(amount, currency);
    return amount + fee;
  }, [calculateServiceFee]);

  const formatCurrency = useCallback((amount, currency, options = {}) => {
    const {
      showSymbol = true,
      decimals = 2
    } = options;

    const symbols = {
      BRL: 'R$',
      ARS: '$'
    };

    const formatted = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(amount);

    return showSymbol ? `${symbols[currency]}${formatted}` : formatted;
  }, []);

  const getCurrencyInfo = useCallback((currency) => {
    const info = {
      BRL: {
        name: 'Real Brasileiro',
        symbol: 'R$',
        decimals: 2,
        flag: '🇧🇷'
      },
      ARS: {
        name: 'Peso Argentino',
        symbol: '$',
        decimals: 2,
        flag: '🇦🇷'
      }
    };

    return info[currency] || null;
  }, []);

  return {
    exchangeRates,
    serviceFee,
    serviceFeeActive,
    selectedCurrencyPair,
    setSelectedCurrencyPair,
    companyFeeSettings,
    convertCurrency,
    getCurrentExchangeRate,
    getCurrencyPairInfo,
    calculateServiceFee,
    calculateTotalWithFee,
    formatCurrency,
    getCurrencyInfo,
    fetchExchangeRates,
    loadCompanyFeeSettings
  };
};

export default useCurrencyConversion;
